/* eslint-disable @angular-eslint/component-class-suffix */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import { CdkMenuModule } from "@angular/cdk/menu";
import { CommonModule } from "@angular/common";
import { Component, computed, inject, model, OnInit } from "@angular/core";
import { RouterModule, RouterOutlet } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import {
    CenterPaymentPermissions,
    CenterRolePermissions,
    CenterTemplatePermissions,
    CenterUserInvitePermissions,
    CenterUserJoinRequestPermissions,
    DefaultPermissions,
} from "@malirang/common";
import {
    AlertService,
    Icon,
    ModalService,
    Tabs,
    ToastService,
} from "@malirang/front-share";
import { CenterIconComponent } from "apps/client/src/components/center/icon/center-icon.component";
import { CenterRoleBadgeComponent } from "apps/client/src/components/center/role-badge/center-role-badge.component";
import { ColorPickerComponent } from "apps/client/src/components/color-picker/color-picker.component";
import { AccessMenuModal } from "apps/client/src/modals/center/access-menu/access-menu.modal";
import { injectNavigationEnd } from "ngxtension/navigation-end";
import { ContainerComponent } from "../../../components/container/container.component";
import { CenterStore } from "../../../stores/center/center.store";
import { SubscribeStore } from "../../../stores/subscribe.store";
import { UserStore } from "../../../stores/user.store";
import { CenterPageAdapter } from "./adapters/center-page.adapter";

type CenterMenu = {
    label: string;
    value: string;
    lock?: boolean;
    count?: number;
};

@Component({
    selector: "app-center-layout",
    templateUrl: "./center.layout.html",
    imports: [
        RouterOutlet,
        ContainerComponent,
        CommonModule,
        CenterRoleBadgeComponent,
        CenterIconComponent,
        RouterModule,
        Icon,
        CdkMenuModule,
        ColorPickerComponent,
        Tabs,
    ],
    styleUrls: ["./center.layout.css"],
})
export class CenterLayout extends CenterPageAdapter implements OnInit {
    readonly userStore = inject(UserStore);
    readonly alertService = inject(AlertService);
    readonly toastService = inject(ToastService);
    readonly modalService = inject(ModalService);
    readonly centerStore = inject(CenterStore);
    readonly subscribeStore = inject(SubscribeStore);
    readonly navigationEnd$ = injectNavigationEnd();

    user = this.userStore.user;
    centerUser = this.centerUserStore.currentCenterUser;

    segment = model("info");

    // 구성원 추가 가능 여부
    isAddUser = computed(() => this.subscribeStore.currentPlan()?.isAddUser);

    /**
     * 데스크탑 센터 메뉴 목록
     */
    options = computed<CenterMenu[]>(() => {
        const myRole = this.roleStore.myRole();

        const isNativeApp = Capacitor.isNativePlatform();

        const base = [
            { label: "센터 정보", value: "info" },
            {
                label: "센터 공지사항",
                value: "notice",
            },
            {
                label: "서비스 관리",
                value: "template",
                lock:
                    !myRole?.permissions.includes(
                        CenterTemplatePermissions.CENTER_TEMPLATE_READ,
                    ) &&
                    !myRole?.permissions.includes(
                        CenterTemplatePermissions.CENTER_TEMPLATE_EDIT,
                    ),
            },
            {
                label: "구성원 역할 관리",
                value: "role",
                lock:
                    !this.isAddUser() ||
                    this.center()?.private ||
                    !myRole?.permissions.includes(
                        CenterRolePermissions.CENTER_ROLE_EDIT,
                    ),
            },
            {
                label: "구성원 초대 · 가입",
                value: "join",
                count:
                    (this.centerStore.count()?.pendingInviteLogsCount || 0) +
                    (this.centerStore.count()?.pendingJoinLogsCount || 0),
                lock:
                    !this.isAddUser() ||
                    this.center()?.private ||
                    !(
                        myRole?.permissions.includes(
                            CenterUserInvitePermissions.CENTER_USER_INVITE_READ,
                        ) ||
                        myRole?.permissions.includes(
                            CenterUserJoinRequestPermissions.CENTER_USER_JOIN_REQUEST_READ,
                        ) ||
                        myRole?.permissions.includes(
                            CenterUserInvitePermissions.CENTER_USER_INVITE,
                        )
                    ),
            },
            { label: "센터 알림", value: "notification" },
            {
                label: "센터 활동 로그",
                value: "log",
                lock: !myRole?.permissions.includes(
                    DefaultPermissions.CENTER_LOG_READ,
                ),
            },
        ];

        if (!isNativeApp) {
            base.push({
                label: "결제 관리",
                value: "payment",
                lock: !myRole?.permissions.includes(
                    CenterPaymentPermissions.CENTER_PAYMENT,
                ),
            });
            base.push({
                label: "구매 내역",
                value: "purchase",
            });
        }

        return base;
    });

    /**
     * 모바일 센터 메뉴 목록
     */
    segmentOptions = computed(() =>
        this.options()
            .filter((o) => !o.lock)
            .map((o) => ({
                label: o.label,
                value: o.value,
            })),
    );

    ngOnInit(): void {
        this.setSegment();

        this.navigationEnd$.subscribe(() => {
            this.setSegment();
        });
    }

    setSegment(): void {
        const found = this.options().find((o) =>
            this.router.url.includes(o.value),
        );

        if (!found?.lock) {
            this.segment.set(found ? found.value : "center");
        }
    }

    onSegmentChange(value: string | undefined): void {
        this.router.navigate(["/center", value]);
    }

    /**
     * 메뉴 클릭 시 권한으로 인해 lock된 경우 클릭 이벤트를 막습니다.
     * @param option
     * @param ev
     */
    handleMenuClick(option: CenterMenu, ev: any) {
        if (option.lock) {
            ev.stopPropagation();
            ev.preventDefault();

            this.modalService.create(AccessMenuModal);
        }
    }

    /**
     * 대표 색상을 변경합니다.
     * @param color
     */
    setColor(color: string) {
        this.centerUserService
            .centerUserControllerSetColor({
                centerId: this.centerId(),
                centerUserId: this.centerUserStore.currentCenterUser()?.id!,
                color,
            })
            .subscribe({
                next: () => {
                    this.centerUserStore.fetchCenterUser();
                    this.centerStore.fetchCenters();
                },
            });
    }

    /**
     * 센터를 탈퇴합니다.
     */
    handleExitCenter() {
        this.alertService
            .open({
                title: "센터 탈퇴",
                content: `탈퇴 시 해당 센터에서의 모든 활동이 중지됩니다.
      센터에서의 활동 데이터는 삭제되지 않으며
      다시 활동하려면 재가입이 필요합니다.`,
                type: "danger",
                buttons: {
                    confirm: {
                        text: "탈퇴",
                        color: "red",
                    },
                },
                data: [
                    { key: "센터명", value: this.center()?.name! },
                    { key: "이름", value: this.user()?.name! },
                    { key: "권한", value: this.roleStore.myRole()?.name! },
                ],
                checkbox: {
                    text: `탈퇴 시 내용을 확인했으며, 탈퇴하겠습니다.`,
                },
            })
            .closed.subscribe({
                next: (result) => {
                    if (result?.action === "confirm") {
                        this.centerUserService
                            .centerUserControllerExit({
                                centerId: this.centerId(),
                                userId: this.user()?.id || "",
                            })
                            .subscribe({
                                next: () => {
                                    window.location.reload();
                                },
                                error: (err) => {
                                    this.toastService.error(err.message);
                                },
                            });
                    }
                },
            });
    }
}
